// Gráfico de linhas com MÚLTIPLAS escalas Y (uma por KPI): até 2 eixos à
// esquerda e 2 à direita, cada um com "auto-zoom" na sua faixa (com folga).
// As linhas de grade são alinhadas — todos os eixos usam o mesmo nº de divisões,
// então cada régua ganha rótulos redondos na altura das mesmas linhas.

export interface KpiAxisSeries {
  key: string
  label: string // rótulo completo (legenda / tooltip)
  short: string // título curto do eixo
  color: string
  side: 'left' | 'right'
  fmtAxis: (n: number) => string // rótulo compacto do eixo
  fmtFull: (n: number) => string // valor completo (com unidade) p/ tooltip/legenda
  target?: number // meta do KPI (linha tracejada) — opcional
  data: Array<{ x: string; y: number | null }>
}

const W = 860
const H = 340
const TOP = 26
const BOTTOM = 42
const AXIS_W = 54 // largura de cada coluna de eixo
const GUTTER = 14
const PAD = 0.08 // folga acima/abaixo dos dados
const TARGET_DIV = 4 // divisões-alvo por eixo (antes do alinhamento)

/** "Nice number" (Heckbert) — arredonda para 1/2/5 × potência de 10. */
function niceNum(range: number, round: boolean): number {
  const exp = Math.floor(Math.log10(range))
  const f = range / Math.pow(10, exp)
  const nf = round
    ? f < 1.5
      ? 1
      : f < 3
        ? 2
        : f < 7
          ? 5
          : 10
    : f <= 1
      ? 1
      : f <= 2
        ? 2
        : f <= 5
          ? 5
          : 10
  return nf * Math.pow(10, exp)
}

/** Base redonda (min, passo, nº de divisões p/ cobrir hi) cobrindo [lo, hi]. */
function baseScale(lo: number, hi: number) {
  if (!(hi > lo)) {
    const p = Math.abs(hi) * 0.1 || 1
    lo -= p
    hi += p
  }
  const range = niceNum(hi - lo, false)
  const step = niceNum(range / TARGET_DIV, true)
  const min = Math.floor(lo / step) * step
  const needed = Math.max(1, Math.ceil((hi - min) / step))
  return { min, step, needed }
}

export function KpiMultiAxisChart({
  series,
  xLabels,
}: {
  series: KpiAxisSeries[]
  xLabels: string[]
}) {
  const hasData =
    series.length > 0 &&
    xLabels.length > 0 &&
    series.some((s) => s.data.some((d) => d.y != null))
  if (!hasData) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-slate-300 text-sm text-slate-400">
        Sem dados de KPIs para a janela selecionada.
      </div>
    )
  }

  // Escala base por série (com folga); depois alinhamos o nº de divisões.
  const bases = series.map((s) => {
    const vals = s.data.map((d) => d.y).filter((v): v is number => v != null)
    // Inclui a meta na faixa para que a linha tracejada fique sempre visível.
    if (s.target != null) vals.push(s.target)
    const dmin = Math.min(...vals)
    const dmax = Math.max(...vals)
    const span = dmax - dmin
    const pad = span > 0 ? span * PAD : Math.abs(dmax) * 0.1 || 1
    return baseScale(dmin - pad, dmax + pad)
  })
  const div = Math.min(7, Math.max(2, ...bases.map((b) => b.needed)))
  const scales = bases.map((b) => ({ min: b.min, step: b.step, max: b.min + div * b.step }))

  const leftAxes = series.map((s, i) => ({ s, i })).filter((a) => a.s.side === 'left')
  const rightAxes = series.map((s, i) => ({ s, i })).filter((a) => a.s.side === 'right')
  const mLeft = leftAxes.length ? leftAxes.length * AXIS_W : GUTTER
  const mRight = rightAxes.length ? rightAxes.length * AXIS_W : GUTTER
  const innerW = W - mLeft - mRight
  const innerH = H - TOP - BOTTOM

  // x de cada eixo: esquerdos crescem para dentro; direitos partem da borda.
  const axisX = (side: 'left' | 'right', pos: number) =>
    side === 'left' ? (pos + 1) * AXIS_W : W - mRight + pos * AXIS_W

  const x = (i: number) =>
    mLeft + (xLabels.length === 1 ? innerW / 2 : (i / (xLabels.length - 1)) * innerW)
  const yOf = (si: number, v: number) => {
    const sc = scales[si]
    return TOP + innerH - ((v - sc.min) / (sc.max - sc.min || 1)) * innerH
  }

  const gridYs = Array.from({ length: div + 1 }, (_, j) => TOP + innerH - (j / div) * innerH)
  const stepX = Math.ceil(xLabels.length / 8)

  const axesToDraw = [
    ...leftAxes.map((a, p) => ({ ...a, side: 'left' as const, pos: p })),
    ...rightAxes.map((a, p) => ({ ...a, side: 'right' as const, pos: p })),
  ]

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
        {/* Grade horizontal (alinhada entre todos os eixos) */}
        {gridYs.map((gy, j) => (
          <line key={j} x1={mLeft} x2={W - mRight} y1={gy} y2={gy} stroke="#eef2f7" strokeWidth={1} />
        ))}
        {/* Base do eixo X */}
        <line
          x1={mLeft}
          x2={W - mRight}
          y1={TOP + innerH}
          y2={TOP + innerH}
          stroke="#cbd5e1"
          strokeWidth={1}
        />
        {/* Rótulos do eixo X (datas dos relatórios) */}
        {xLabels.map((lbl, i) =>
          i % stepX === 0 || i === xLabels.length - 1 ? (
            <text key={i} x={x(i)} y={H - 14} textAnchor="middle" className="fill-slate-400 text-[10px]">
              {lbl.slice(8, 10)}/{lbl.slice(5, 7)}
            </text>
          ) : null
        )}

        {/* Eixos Y — um por KPI, na cor do KPI */}
        {axesToDraw.map(({ s, i, side, pos }) => {
          const lineX = axisX(side, pos)
          const sc = scales[i]
          return (
            <g key={s.key}>
              <line
                x1={lineX}
                x2={lineX}
                y1={TOP}
                y2={TOP + innerH}
                stroke={s.color}
                strokeOpacity={0.45}
                strokeWidth={1}
              />
              <text
                x={lineX}
                y={TOP - 10}
                textAnchor="middle"
                className="text-[10px] font-semibold"
                fill={s.color}
              >
                {s.short}
              </text>
              {gridYs.map((gy, j) => (
                <text
                  key={j}
                  x={side === 'left' ? lineX - 6 : lineX + 6}
                  y={gy + 3}
                  textAnchor={side === 'left' ? 'end' : 'start'}
                  className="text-[9px]"
                  fill={s.color}
                  fillOpacity={0.85}
                >
                  {s.fmtAxis(sc.min + j * sc.step)}
                </text>
              ))}
            </g>
          )
        })}

        {/* Metas — linha tracejada por KPI (na escala do próprio KPI) */}
        {series.map((s, si) =>
          s.target == null ? null : (
            <g key={`meta-${s.key}`}>
              <line
                x1={mLeft}
                x2={W - mRight}
                y1={yOf(si, s.target)}
                y2={yOf(si, s.target)}
                stroke={s.color}
                strokeWidth={1.5}
                strokeDasharray="5 4"
                strokeOpacity={0.85}
              />
              <text
                x={W - mRight - 4}
                y={yOf(si, s.target) - 3}
                textAnchor="end"
                className="text-[9px] font-semibold"
                fill={s.color}
                stroke="#fff"
                strokeWidth={3}
                paintOrder="stroke"
              >
                meta {s.fmtAxis(s.target)}
              </text>
            </g>
          )
        )}

        {/* Linhas (cada uma na sua escala) */}
        {series.map((s, si) => {
          const pts = s.data
            .map((d, i) => (d.y == null ? null : `${x(i)},${yOf(si, d.y)}`))
            .filter((p): p is string => p != null)
          return (
            <g key={s.key}>
              <polyline points={pts.join(' ')} fill="none" stroke={s.color} strokeWidth={2} />
              {s.data.map((d, i) =>
                d.y == null ? null : (
                  <circle key={i} cx={x(i)} cy={yOf(si, d.y)} r={3} fill={s.color}>
                    <title>
                      {s.label} · {d.x}: {s.fmtFull(d.y)}
                    </title>
                  </circle>
                )
              )}
            </g>
          )
        })}

        {/* Rótulos de dados — valor em cada ponto, na cor do KPI (halo branco p/ legibilidade) */}
        {series.map((s, si) =>
          s.data.map((d, i) =>
            d.y == null ? null : (
              <text
                key={`${s.key}-lbl-${i}`}
                x={x(i)}
                y={yOf(si, d.y) + (si % 2 === 0 ? -7 : 13)}
                textAnchor={i === 0 ? 'start' : i === xLabels.length - 1 ? 'end' : 'middle'}
                className="text-[9px] font-semibold"
                fill={s.color}
                stroke="#fff"
                strokeWidth={3}
                paintOrder="stroke"
              >
                {s.fmtAxis(d.y)}
              </text>
            )
          )
        )}
      </svg>

      {/* Legenda com a faixa real (mín → máx) de cada KPI */}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {series.map((s) => {
          const vals = s.data.map((d) => d.y).filter((v): v is number => v != null)
          const lo = Math.min(...vals)
          const hi = Math.max(...vals)
          return (
            <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
              {s.label}
              <span className="text-slate-400">
                ({s.fmtFull(lo)}
                {lo !== hi ? ` → ${s.fmtFull(hi)}` : ''})
              </span>
            </span>
          )
        })}
      </div>
    </>
  )
}
